import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Dropdown } from "react-bootstrap";
import type { PlanId, PlanLimits } from "../../../api/organizations";
import type { OrganizationRecord } from "../../../api/organizations";
import { formatDate, formatDaysFromNow, isPlanExpired } from "../utils";
import { statusVariant } from "../constants";
import { dropdownPopperConfig } from "../dropdownPopperConfig";

const PhoneWithCopy: React.FC<{ phone?: string }> = ({ phone }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!phone) return;
      try {
        await navigator.clipboard.writeText(phone);
        setCopied(true);
      } catch {
        // Fallback for older browsers / insecure contexts
        const input = document.createElement("input");
        input.value = phone;
        input.setAttribute("readonly", "");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopied(true);
      }
    },
    [phone],
  );

  if (!phone) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div className="org-phone-copy">
      <span className="org-phone-copy__number" title={phone}>
        {phone}
      </span>
      <Button
        type="button"
        variant="outline-secondary"
        size="sm"
        className="org-phone-copy__btn"
        onClick={handleCopy}
        aria-label={copied ? "Phone copied" : `Copy phone ${phone}`}
        title={copied ? "Copied!" : "Copy phone"}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
};

interface OrganizationsTableProps {
  organizations: OrganizationRecord[];
  planCatalog: Record<PlanId, PlanLimits>;
  loading: boolean;
  currentPage: number;
  hasMore: boolean;
  actionBusyId: string | null;
  onSetPlan: (org: OrganizationRecord) => void;
  onStartTrial: (org: OrganizationRecord) => void;
  onDeactivate: (org: OrganizationRecord) => void;
  onToggleActive: (org: OrganizationRecord) => void;
  onOpenInAdmin: (org: OrganizationRecord) => void;
  onDeleteOrganization: (org: OrganizationRecord) => void;
  onManageIntelligence: (org: OrganizationRecord) => void;
  onViewTopClients: (org: OrganizationRecord) => void;
  onOpenActivity: (org: OrganizationRecord) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
}

const OrgActionsDropdown: React.FC<{
  org: OrganizationRecord;
  actionBusyId: string | null;
  onOpenInAdmin: (org: OrganizationRecord) => void;
  onSetPlan: (org: OrganizationRecord) => void;
  onStartTrial: (org: OrganizationRecord) => void;
  onDeactivate: (org: OrganizationRecord) => void;
  onToggleActive: (org: OrganizationRecord) => void;
  onDeleteOrganization: (org: OrganizationRecord) => void;
  onManageIntelligence: (org: OrganizationRecord) => void;
  onViewTopClients: (org: OrganizationRecord) => void;
  onOpenActivity: (org: OrganizationRecord) => void;
  toggleClassName?: string;
}> = ({
  org,
  actionBusyId,
  onOpenInAdmin,
  onSetPlan,
  onStartTrial,
  onDeactivate,
  onToggleActive,
  onDeleteOrganization,
  onManageIntelligence,
  onViewTopClients,
  onOpenActivity,
  toggleClassName,
}) => (
  <Dropdown
    align="end"
    className="org-actions-dropdown"
    onClick={(e) => e.stopPropagation()}
  >
    <Dropdown.Toggle
      variant="outline-secondary"
      size="sm"
      id={`actions-${org.id}`}
      className={toggleClassName}
    >
      Actions
    </Dropdown.Toggle>
    <Dropdown.Menu
      popperConfig={dropdownPopperConfig}
      renderOnMount
      className="org-actions-dropdown__menu"
    >
      <Dropdown.Item onClick={() => onOpenActivity(org)}>
        View activity…
      </Dropdown.Item>
      <Dropdown.Item onClick={() => onOpenInAdmin(org)}>
        Open in admin
      </Dropdown.Item>
      <Dropdown.Item onClick={() => onViewTopClients(org)}>
        Top clients…
      </Dropdown.Item>
      <Dropdown.Divider className="bg-secondary" />
      <Dropdown.Item onClick={() => onSetPlan(org)}>
        Set plan &amp; status
      </Dropdown.Item>
      <Dropdown.Item onClick={() => onManageIntelligence(org)}>
        Intelligence…
      </Dropdown.Item>
      <Dropdown.Item
        onClick={() => onStartTrial(org)}
        disabled={
          actionBusyId === org.id || org.subscriptionStatus === "trialing"
        }
      >
        {actionBusyId === org.id ? "…" : "Start / extend trial"}
      </Dropdown.Item>
      <Dropdown.Item
        onClick={() => onDeactivate(org)}
        disabled={actionBusyId === org.id}
      >
        Deactivate plan
      </Dropdown.Item>
      <Dropdown.Divider className="bg-secondary" />
      <Dropdown.Item
        onClick={() => onToggleActive(org)}
        disabled={actionBusyId === org.id}
      >
        {org.active ? "Deactivate org" : "Activate org"}
      </Dropdown.Item>
      <Dropdown.Divider className="bg-secondary" />
      <Dropdown.Item
        onClick={() => onDeleteOrganization(org)}
        disabled={actionBusyId === org.id}
        className="text-danger"
      >
        Delete organization…
      </Dropdown.Item>
    </Dropdown.Menu>
  </Dropdown>
);

const OrganizationMobileCard: React.FC<{
  org: OrganizationRecord;
  actionBusyId: string | null;
  onOpenInAdmin: (org: OrganizationRecord) => void;
  onSetPlan: (org: OrganizationRecord) => void;
  onStartTrial: (org: OrganizationRecord) => void;
  onDeactivate: (org: OrganizationRecord) => void;
  onToggleActive: (org: OrganizationRecord) => void;
  onDeleteOrganization: (org: OrganizationRecord) => void;
  onManageIntelligence: (org: OrganizationRecord) => void;
  onViewTopClients: (org: OrganizationRecord) => void;
  onOpenActivity: (org: OrganizationRecord) => void;
}> = (props) => {
  const { org, onOpenActivity } = props;
  const status = org.subscriptionStatus ?? "—";
  const planExpired = isPlanExpired(org);

  return (
    <article
      className={`org-mobile-card org-mobile-card--clickable${
        planExpired ? " org-mobile-card--plan-expired" : ""
      }`}
      data-plan-expired={planExpired ? "true" : "false"}
      onClick={() => onOpenActivity(org)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenActivity(org);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${
        planExpired ? "PLAN EXPIRED. " : ""
      }View activity for ${org.name}`}
    >
      <header className="org-mobile-card__header">
        <h3 className="org-mobile-card__name">{org.name}</h3>
        <p className="org-mobile-card__meta">{org.email || org.id}</p>
        <div className="org-mobile-card__badges">
          {planExpired ? (
            <Badge bg="danger" className="org-plan-expired-badge">
              PLAN EXPIRED
            </Badge>
          ) : null}
          <Badge bg={statusVariant[status] ?? "secondary"}>{status}</Badge>
          <Badge bg={org.active ? "success" : "secondary"}>
            {org.active ? "Active" : "Inactive"}
          </Badge>
        </div>
      </header>
      <div className="org-mobile-card__body">
        <div className="org-mobile-card__row">
          <span className="org-mobile-card__row-label">Phone</span>
          <span
            className="org-mobile-card__row-value"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <PhoneWithCopy phone={org.phone} />
          </span>
        </div>
        <div className="org-mobile-card__row">
          <span className="org-mobile-card__row-label">Created</span>
          <span className="org-mobile-card__row-value">
            {formatDate(org.created)}
          </span>
        </div>
        <div className="org-mobile-card__row">
          <span className="org-mobile-card__row-label">Plan</span>
          <span className="org-mobile-card__row-value">
            {org.plan?.name ?? org.planId ?? "—"}
          </span>
        </div>
        <div className="org-mobile-card__row">
          <span className="org-mobile-card__row-label">Intelligence</span>
          <span className="org-mobile-card__row-value">
            {org.intelligenceCapacityId ? (
              <>
                {org.intelligenceCapacityId.toUpperCase()}
                {org.intelligenceTrialing ? " · trial" : ""}
                {typeof org.intelligenceCreditsUsed === "number" &&
                typeof org.intelligenceCreditsAllowance === "number"
                  ? ` · ${org.intelligenceCreditsUsed}/${org.intelligenceCreditsAllowance}`
                  : ""}
              </>
            ) : (
              "—"
            )}
          </span>
        </div>
        <div className="org-mobile-card__row">
          <span className="org-mobile-card__row-label">Period end</span>
          <span
            className={`org-mobile-card__row-value${
              planExpired ? " org-plan-expired-period" : ""
            }`}
          >
            {formatDate(org.currentPeriodEnd)}
            {planExpired && org.currentPeriodEnd ? (
              <span className="org-plan-expired-period__hint">
                {" "}
                · {formatDaysFromNow(org.currentPeriodEnd)}
              </span>
            ) : null}
          </span>
        </div>
      </div>
      <footer
        className="org-mobile-card__footer"
        onClick={(e) => e.stopPropagation()}
      >
        <OrgActionsDropdown
          {...props}
          toggleClassName="w-100"
        />
      </footer>
    </article>
  );
};

export const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
  organizations,
  loading,
  currentPage,
  hasMore,
  actionBusyId,
  onSetPlan,
  onStartTrial,
  onDeactivate,
  onToggleActive,
  onOpenInAdmin,
  onDeleteOrganization,
  onManageIntelligence,
  onViewTopClients,
  onOpenActivity,
  onPrevPage,
  onNextPage,
}) => {
  const actionProps = {
    actionBusyId,
    onOpenInAdmin,
    onSetPlan,
    onStartTrial,
    onDeactivate,
    onToggleActive,
    onDeleteOrganization,
    onManageIntelligence,
    onViewTopClients,
    onOpenActivity,
  };

  return (
    <>
      <div className="org-mobile-list">
        {organizations.map((org) => (
          <OrganizationMobileCard key={org.id} org={org} {...actionProps} />
        ))}
      </div>

      <div className="org-table-desktop table-responsive">
        <table className="table table-sm mb-0 align-middle">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Intelligence</th>
              <th>Period end</th>
              <th>Active</th>
              <th className="org-table-desktop__actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => {
              const planExpired = isPlanExpired(org);
              return (
                <tr
                  key={org.id}
                  className={`org-table-desktop__row${
                    planExpired ? " org-table-desktop__row--plan-expired" : ""
                  }`}
                  data-plan-expired={planExpired ? "true" : "false"}
                  onClick={() => onOpenActivity(org)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenActivity(org);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${
                    planExpired ? "PLAN EXPIRED. " : ""
                  }View activity for ${org.name}`}
                >
                  <td>
                    <div className="fw-semibold">{org.name}</div>
                    <div className="text-muted small">
                      {org.email || org.id}
                    </div>
                    <div className="org-org-meta">
                      <PhoneWithCopy phone={org.phone} />
                      <span className="text-muted small text-nowrap">
                        Created {formatDate(org.created)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column align-items-start gap-1">
                      {planExpired ? (
                        <Badge bg="danger" className="org-plan-expired-badge">
                          PLAN EXPIRED
                        </Badge>
                      ) : null}
                      <Badge
                        bg={
                          statusVariant[org.subscriptionStatus ?? ""] ??
                          "secondary"
                        }
                      >
                        {org.subscriptionStatus ?? "—"}
                      </Badge>
                    </div>
                  </td>
                  <td>
                    {org.plan ?? org.planId ? (
                      <>
                        <span>{org.plan?.name ?? org.planId ?? "—"}</span>
                        <div className="text-muted small">
                          {org.planId ?? org.plan?.id ?? "—"}
                        </div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {org.intelligenceCapacityId ? (
                      <>
                        <Badge
                          bg={org.intelligenceTrialing ? "info" : "success"}
                          className="text-uppercase"
                        >
                          {org.intelligenceCapacityId}
                          {org.intelligenceTrialing ? " trial" : ""}
                        </Badge>
                        {typeof org.intelligenceCreditsUsed === "number" &&
                        typeof org.intelligenceCreditsAllowance === "number" ? (
                          <div className="text-muted small">
                            {org.intelligenceCreditsUsed}/
                            {org.intelligenceCreditsAllowance} cr
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td
                    className={`small${
                      planExpired
                        ? " org-plan-expired-period"
                        : " text-muted"
                    }`}
                  >
                    <div>{formatDate(org.currentPeriodEnd)}</div>
                    {planExpired && org.currentPeriodEnd ? (
                      <div className="org-plan-expired-period__hint">
                        {formatDaysFromNow(org.currentPeriodEnd)}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <Badge bg={org.active ? "success" : "secondary"}>
                      {org.active ? "Yes" : "No"}
                    </Badge>
                  </td>
                  <td
                    className="org-table-desktop__actions-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <OrgActionsDropdown org={org} {...actionProps} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="org-pagination">
        <p className="org-pagination__meta mb-0">
          Page {currentPage}
          {hasMore ? " · More pages available" : ""}
        </p>
        <div className="org-pagination__buttons">
          <Button
            variant="outline-secondary"
            disabled={currentPage <= 1 || loading}
            onClick={onPrevPage}
          >
            Previous
          </Button>
          <Button
            variant="outline-secondary"
            disabled={!hasMore || loading}
            onClick={onNextPage}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};
